import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, Outlet } from 'react-router-dom';
import { AffiliateTracker } from 'src/components/AffiliateTracker/AffiliateTracker';
import { DEFAULT_LANGUAGE, isValidLanguageCode, addLanguageToPath, removeLanguageFromPath } from 'src/utils/languages';

/**
 * LanguageRoute component that:
 * 1. Gets language from URL params (/:lang)
 * 2. Redirects to language-prefixed URL if no language param exists
 * 3. Updates i18n when language changes
 */
export default function LanguageRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();

  useEffect(() => {
    const langFromParams = params.lang?.toLowerCase();
    const pathname = location.pathname;

    // If we're on a route without language param (catch-all), redirect to add language
    if (!langFromParams) {
      // Try to get language from i18n (might be from localStorage or browser)
      const detectedLang = i18n.language || DEFAULT_LANGUAGE;
      const validLang = isValidLanguageCode(detectedLang) ? detectedLang : DEFAULT_LANGUAGE;

      // Add language prefix to current path
      const pathWithoutLang = removeLanguageFromPath(pathname);
      const newPath = addLanguageToPath(pathWithoutLang, validLang);

      // Redirect to language-prefixed URL, preserving query string so that
      // parameters like ?ref= (affiliate) and UTM tags are not lost.
      navigate(
        {
          pathname: newPath,
          search: location.search,
        },
        { replace: true }
      );
      return;
    }

    // Validate language code
    if (!isValidLanguageCode(langFromParams)) {
      // Invalid language code, redirect to default language
      const pathWithoutLang = removeLanguageFromPath(pathname);
      const newPath = addLanguageToPath(pathWithoutLang, DEFAULT_LANGUAGE);
      navigate(
        {
          pathname: newPath,
          search: location.search,
        },
        { replace: true }
      );
      return;
    }

    // If language is in params, update i18n
    if (langFromParams && i18n.language !== langFromParams) {
      void i18n.changeLanguage(langFromParams);
    }

    const searchParams = new URLSearchParams(location.search);
    const googleSsoEmail = searchParams.get('googleSsoEmail');
    if (googleSsoEmail) {
      searchParams.delete('googleSsoEmail');
      searchParams.set('email', googleSsoEmail);
      navigate(
        {
          pathname: addLanguageToPath('/google/callback', langFromParams),
          search: `?${searchParams.toString()}`,
        },
        { replace: true }
      );
    }
  }, [params.lang, location.pathname, location.search, navigate, i18n]);

  // The redirect above runs after render. Do not show a nested 404 while an
  // unprefixed OAuth callback or /home URL is being localized.
  if (!params.lang || !isValidLanguageCode(params.lang.toLowerCase())) {
    return null;
  }

  return (
    <>
      <AffiliateTracker />
      <Outlet />
    </>
  );
}
